"use client";

import React, { useEffect, useState, useCallback } from "react";
import { Quote, Star } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/ui/carousel";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const testimonials = [{
  author: "Marcus Chen",
  quote: "The AI support agents resolved our most complex network architecture issues in seconds. The efficiency gain is simply unprecedented.",
  position: "CTO, NexaGen Systems"
}, {
  author: "Sarah Jenkins",
  quote: "We've reduced our support overhead by 85% while increasing customer satisfaction. It's the future of technical infrastructure.",
  position: "Operations Director, CloudScale"
}];
export default function VoicesOfEfficiency() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!api) return;
    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);
    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);
  const scrollTo = useCallback((index: number) => api?.scrollTo(index), [api]);
  return <section data-section-id="237377" id="voices-of-efficiency" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden relative" ref={ref}>
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-primary/10 rounded-lg blur-[120px] pointer-events-none" />

      <div className={cn("container mx-auto px-4 md:px-6 transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
        <div className="text-center mb-16 md:mb-24">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4 tracking-tighter uppercase">
            Voices of Efficiency
          </h2>
          <div className="w-12 h-1 bg-primary mx-auto rounded-md opacity-100" />
        </div>

        <div className="max-w-5xl mx-auto">
          <Carousel setApi={setApi} opts={{
          align: "center",
          loop: true
        }} className="w-full">
            <CarouselContent>
              {testimonials.map((item, i) => <CarouselItem key={i} data-index={i}>
                  <div className="flex flex-col items-center text-center px-4 md:px-12">
                    <Quote className="size-16 md:size-20 text-primary mb-8 opacity-90 drop-shadow-[0_0_8px_rgba(0,255,242,0.4)]" />
                    
                    <p className="text-xl md:text-3xl font-serif italic text-foreground leading-relaxed mb-10 max-w-4xl">
                      "{item.quote}"
                    </p>
                    
                    <div className="flex gap-1.5 mb-8">
                      {[...Array(5)].map((_, starIndex) => <Star key={starIndex} data-index={starIndex} className="size-5 md:size-6 fill-primary text-primary" />)}
                    </div>
                    
                    <div className="flex flex-col items-center">
                      <div className="size-16 rounded-md border-2 border-primary/30 p-1 mb-4 shadow-md">
                        <img src={`/img/client_avatar_${i}.png`} alt={item.author} className="w-full h-full object-cover rounded-md" />
                      </div>
                      <span className="font-default font-bold text-lg md:text-xl text-foreground tracking-tight">
                        {item.author}
                      </span>
                      <span className="text-muted-foreground text-sm md:text-base mt-1.5 font-mono font-medium tracking-widest uppercase">
                        {item.position}
                      </span>
                    </div>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
          </Carousel>

          <div className="flex justify-center gap-3 mt-16 md:mt-20">
            {Array.from({
            length: count
          }).map((_, i) => <Button key={i} data-index={i} variant="ghost" className={cn("h-2.5 p-0 min-w-0 rounded-md transition-all duration-500 hover:bg-primary/80 hover:text-transparent", current === i + 1 ? "bg-primary w-12 shadow-[0_0_15px_rgba(0,255,242,0.6)]" : "bg-secondary w-2.5")} onClick={() => scrollTo(i)} aria-label={`Go to slide ${i + 1}`}>
                <span className="sr-only">Slide {i + 1}</span>
              </Button>)}
          </div>
        </div>
      </div>
    </section>;
}